import { queryKey } from '../constants/queryClientKey';
import {
  createQMS,
  deleteQMS,
  getQMS,
  getQMSDetail,
  updateQMS,
  pushQMS,
  getQMSProfile,
  updateQMSProfile,
  updateQueueItem,
  updateQMSItemPosition,
} from '../apis/qms.api';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import {
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export function useGetQMS(
  params: Parameters<typeof getQMS>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.qms, sortObjectKeys(params)],
    () => getQMS(params),
    {
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getQMS>>>;
}

export function useGetQMSDetail(
  id: string,
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.qms_detail, id],
    () => getQMSDetail(id),
    {
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getQMSDetail>>>;
}

export function useCreateQMS() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: Parameters<typeof createQMS>[0]) => createQMS(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.qms]);
      },
      onError: (err) => {
        console.error('useCreateQMS:', err);
      },
    }
  );
}

type ParamsUpdateQMS = Parameters<typeof updateQMS>;
export function useUpdateQMS() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      data,
    }: {
      id: ParamsUpdateQMS[0];
      data: ParamsUpdateQMS[1];
    }) => updateQMS(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.qms]);
      },

      onError: (err) => {
        console.error('useUpdateQMS:', err);
      },
    }
  );
}

export function useDeleteQMS() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteQMS(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.qms]);
    },
    onError: (err) => {
      console.error('useDeleteQMS:', err);
    },
  });
}

export function usePushQMS() {
  const queryClient = useQueryClient();

  return useMutation(async ({ qms_id, institution_id, order_ids }: { qms_id: string, institution_id: string, order_ids: string[] }) => pushQMS({ qms_id, institution_id, order_ids }), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.qms]);
    },
    onError: (err) => {
      console.error('usePushQMS:', err);
    },
  });
}

export function useGetQMSProfile(options?: UseQueryOptions) {
  return useQuery(
    [queryKey.qms_profile],
    () => getQMSProfile(),
    {
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getQMSProfile>>>;
}

export function useUpdateQMSProfile() {
  const queryClient = useQueryClient();

  return useMutation(async (data: Parameters<typeof updateQMSProfile>[0]) => updateQMSProfile(data), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.qms_profile]);
      queryClient.invalidateQueries([queryKey.qms]);
    },
    onError: (err) => {
      console.error('useUpdateQMSProfile:', err);
    },
  });
}

export function useUpdateQueueItem() {
  const queryClient = useQueryClient();

  return useMutation(async ({ id, data }: { id: string; data: Parameters<typeof updateQueueItem>[1] }) => updateQueueItem(id, data), {
    onSuccess: () => {
      // queryClient.invalidateQueries([queryKey.qms]);
    },
    onError: (err) => {
      console.error('useUpdateQueueItem:', err);
    },
  });
}

export function useUpdateQMSItemPosition() {
  const queryClient = useQueryClient();

  return useMutation(async ({ id, data }: { id: string; data: Parameters<typeof updateQMSItemPosition>[1] }) => updateQMSItemPosition(id, data), {
    onSuccess: () => {
      // queryClient.invalidateQueries([queryKey.qms]);
    },
    onError: (err) => {
      console.error('useUpdateQueueItem:', err);
    },
  });
}
